import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Share2 } from "lucide-react";
import { api, setPageMeta, setJsonLd, SITE_BASE } from "@/lib/api";
import { Reveal } from "@/components/Motion";
import { VideoTile } from "@/components/VideoTile";
import { JoinForm } from "@/components/JoinForm";
import { Breadcrumbs } from "@/components/Breadcrumbs";

export default function StoryPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.get(`/stories/${slug}`)
      .then((r) => {
        setData(r.data);
        const s = r.data.story;
        setPageMeta(`${s.title} | Stories | Ninja Run Club`, s.excerpt, {
          type: "article",
          published: s.date,
          modified: s.date,
          ...(s.hero?.poster || s.hero?.type === "image" ? { image: s.hero.poster || s.hero.src, imageAlt: s.title } : {}),
        });
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  // Audit NRC-061/067: BlogPosting + VideoObject structured data
  useEffect(() => {
    if (!data) return;
    const { story } = data;
    const ld = [
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: story.title,
        description: story.excerpt,
        datePublished: story.date,
        author: { "@type": "Organization", name: story.author || "NRC Editorial Team", url: `${SITE_BASE}/about` },
        publisher: { "@type": "Organization", name: "Ninja Run Club", url: SITE_BASE, logo: { "@type": "ImageObject", url: `${SITE_BASE}/brand/nr-mark.png` } },
        mainEntityOfPage: `${SITE_BASE}/stories/${story.slug}`,
        ...(story.hero?.poster || story.hero?.src ? { image: `${SITE_BASE}${story.hero.poster || story.hero.src}` } : {}),
      },
    ];
    const videos = [
      ...(story.hero?.type === "video" ? [{ ...story.hero, caption: story.title }] : []),
      ...(story.media || []),
    ].filter((m) => m.src && m.src.endsWith(".mp4"));
    videos.forEach((m) => {
      ld.push({
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: m.caption || story.title,
        description: `${m.caption ? m.caption + ". " : ""}Footage from "${story.title}" — Ninja Run Club. No dialogue; ambient event footage.`,
        contentUrl: `${SITE_BASE}${m.src}`,
        ...(m.poster ? { thumbnailUrl: `${SITE_BASE}${m.poster}` } : {}),
        uploadDate: story.date,
      });
    });
    return setJsonLd("story-jsonld", ld);
  }, [data]);

  useEffect(() => {
    if (notFound) setPageMeta("Story Not Found | Ninja Run Club", "", { noindex: true });
  }, [notFound]);

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-5">
        <p className="font-display text-4xl">STORY NOT FOUND.</p>
        <Link to="/stories" className="font-mono-label link-underline text-white/60">ALL STORIES</Link>
      </div>
    );
  }
  if (!data) return <p className="font-mono-label text-white/40 py-32 text-center">LOADING...</p>;

  const { story, related } = data;

  const share = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied.");
  };

  return (
    <div className="bg-night min-h-screen">
      <header className="px-5 lg:px-10 pt-10 pb-10 border-b border-white/10 bg-ink">
        <Breadcrumbs light items={[["Home", "/"], ["Stories", "/stories"], [story.title, `/stories/${story.slug}`]]} />
        <Link to="/stories" className="mt-5 inline-flex items-center gap-2 font-mono-label text-white/40 hover:text-white transition-colors duration-200">
          <ArrowLeft size={13} /> STORIES
        </Link>
        <Reveal>
          <p className="font-mono-label text-white/50 mt-8">
            {story.category} · <time dateTime={story.date}>{story.date}</time>
            {story.author && <span> · BY {story.author.toUpperCase()}</span>}
          </p>
          <h1 data-testid="story-title" className="font-display text-3xl sm:text-4xl lg:text-6xl mt-4 max-w-4xl leading-[0.98]">{story.title}</h1>
          <button onClick={share} data-testid="story-share-button" className="mt-6 flex items-center gap-1.5 font-mono-label text-white/40 hover:text-white transition-colors duration-200">
            <Share2 size={12} /> SHARE
          </button>
        </Reveal>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 border-b border-white/10">
        <div className="lg:col-span-5 border-r border-white/10">
          <div className="lg:sticky lg:top-16">
            {story.hero?.type === "video" ? (
              <VideoTile src={story.hero.src} poster={story.hero.poster} className="aspect-[4/5]" testid="story-hero-video" />
            ) : (
              <img src={story.hero?.src} alt={story.title} width="1920" height="2880" loading="lazy" className="img-bw w-full aspect-[4/5] object-cover" />
            )}
          </div>
        </div>
        <article className="lg:col-span-7 px-5 lg:px-14 py-12 max-w-3xl">
          {story.short_answer && (
            <div data-testid="story-short-answer" className="border-l-2 border-white pl-6 py-2 mb-10">
              <p className="font-mono-label text-white/40 mb-3">THE SHORT VERSION</p>
              <p className="text-white/90 text-base lg:text-lg leading-relaxed">{story.short_answer}</p>
            </div>
          )}
          {story.body.map((p, i) =>
            p.startsWith("## ") ? (
              <h2 key={`${i}-${p.slice(3, 30)}`} className="font-display text-2xl lg:text-3xl mt-10 mb-5">{p.slice(3).toUpperCase()}</h2>
            ) : (
              <p key={`${i}-${p.slice(0, 30)}`} className={`leading-relaxed mb-6 ${i === 0 ? "text-white/90 text-lg" : "text-white/65 text-sm lg:text-base"}`}>{p}</p>
            )
          )}
          {(story.takeaways || []).length > 0 && (
            <section className="border border-white/15 p-7 mt-10" data-testid="story-takeaways">
              <h2 className="font-mono-label text-white/40 mb-5">KEY TAKEAWAYS</h2>
              <ul className="space-y-3">
                {story.takeaways.map((t, i) => (
                  <li key={t} className="flex gap-4 text-white/80 text-sm">
                    <span className="font-mono-label text-white/30">0{i + 1}</span> {t}
                  </li>
                ))}
              </ul>
            </section>
          )}
          {(story.related_training || []).length > 0 && (
            <div className="mt-8 flex flex-wrap gap-3" data-testid="story-related-training">
              {story.related_training.map((l) => (
                <Link key={l.path} to={l.path} className="btn-invert font-mono-label border border-white/30 px-4 py-2.5 hover:bg-white hover:text-ink">
                  {l.label.toUpperCase()} →
                </Link>
              ))}
            </div>
          )}
        </article>
      </div>

      {(story.media || []).length > 0 && (
        <section className="px-5 lg:px-10 py-14 border-b border-white/10" data-testid="story-media-strip">
          <h2 className="font-mono-label text-white/40 mb-8">FROM THE FOOTAGE</h2>
          <div className={`grid gap-px bg-white/10 border border-white/10 grid-cols-2 ${story.media.length > 2 ? "lg:grid-cols-4" : "lg:grid-cols-2"}`}>
            {story.media.map((m, i) => (
              <VideoTile key={m.src || i} src={m.src} poster={m.poster} caption={m.caption} className="aspect-[4/5]" testid={`story-media-${i}`} />
            ))}
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section className="px-5 lg:px-10 py-14 border-b border-white/10">
          <h2 className="font-mono-label text-white/40 mb-8">MORE STORIES</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {related.map((s) => (
              <Link key={s.slug} to={`/stories/${s.slug}`} className="group bg-night p-8 hover:bg-white hover:text-ink transition-colors duration-300">
                <p className="font-mono-label opacity-50">{s.category}</p>
                <h3 className="font-display text-xl lg:text-2xl mt-3">{s.title}</h3>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="light-section bg-paper text-ink px-5 lg:px-10 py-20">
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl lg:text-4xl">BE IN THE NEXT ONE.</h2>
          <p className="text-ink/60 text-sm mt-3 mb-8">Every story starts with showing up. Get the next run in your inbox.</p>
          <JoinForm compact source={`story-${slug}`} />
        </div>
      </section>
    </div>
  );
}
